import * as _m from '@/models';
import { useEffect, useRef, useState } from 'react';
import { DeltaJsonElement } from '@/components/common/DeltaJsonElement';
import { useChannelPost } from '@/hooks/api/useChannelPost';
import { useChannelPostComments } from '@/hooks/api/useChannelPostComments';
import { SimpleTextFormat } from '@/components/common/SimpleTextFormat';
import ReactTimeAgo from 'react-time-ago';
import { useCreateChannelPostComment } from '@/hooks/api/useCreateChannelPostComment';
import { toastApiError, toastError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { storage } from '@/lib/storage';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

type Props = {
  channel: _m.Channel;
  channel_post: _m.ChannelPost;
};

// fingerprintのエージェントロードはコストが高いため、モジュールスコープでキャッシュして再マウント時の再ロードを避ける
let fpAgentPromise: ReturnType<typeof FingerprintJS.load> | null = null;
const getFingerprintAgent = () => {
  if (!fpAgentPromise) {
    fpAgentPromise = FingerprintJS.load().catch((e) => {
      fpAgentPromise = null;
      throw e;
    });
  }
  return fpAgentPromise;
};

// 拡張機能等でCanvas/WebGL APIがブロックされている場合は取得できないため、その場合はundefinedとして扱う
const fetchFingerprint = async (): Promise<string | undefined> => {
  try {
    const fp = await getFingerprintAgent();
    const result = await fp.get();
    return result.visitorId;
  } catch (e) {
    console.warn('fingerprint calculation failed', e);
    return undefined;
  }
};

export const ChannelPostComments = ({ channel, channel_post }: Props) => {
  const { setShowLoading } = useLoading();
  const [commentContent, setCommentContent] = useState('');
  const { data: comments, isError: isChannelPostError } = useChannelPostComments(channel.cid, channel_post.id);
  const [createdComments, setCreatedComments] = useState<_m.Comment[]>([]);
  const { mutate, data, error, isPending } = useCreateChannelPostComment();

  const [visitorId, setVisitorId] = useState<string | undefined>(undefined);
  useEffect(() => {
    fetchFingerprint().then(setVisitorId);
  }, []);

  const onCommentSendClick = async () => {
    setShowLoading(true);
    const fingerprint = visitorId ?? (await fetchFingerprint());
    mutate({
      cid: channel.cid,
      id: channel_post.id,
      content: commentContent,
      fingerprint,
      device_id: storage.deviceId.get(),
    });
  };

  useEffect(() => setShowLoading(isPending), [isPending]);

  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (data) {
      setCommentContent('');
      createdComments.unshift(data);
    }
  }, [data]);
  if (comments == null) return <></>;

  return (
    <section className="post-comments">
      <div className="control-group">
        {createdComments.length + channel_post.comments_count > 0 && (
          <div className="label">{createdComments.length + channel_post.comments_count}件のコメント</div>
        )}

        <div className="control">
          <textarea
            maxLength={200}
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
          ></textarea>
        </div>
      </div>
      <div className="control-group submit">
        <button className={`btn ${commentContent.length > 0 ? '' : 'disabled'}`} onClick={onCommentSendClick}>
          コメント
        </button>
      </div>
      <ul className="comments">
        {createdComments?.map((c) => (
          <li key={`c-${c.id}`}>
            <div className="time">
              <ReactTimeAgo date={new Date(c.created_at)} locale="ja" />
            </div>
            <p>
              <SimpleTextFormat content={c.content} />
            </p>
          </li>
        ))}
        {comments?.map((c) => (
          <li key={`c-${c.id}`}>
            <div className="time">
              <ReactTimeAgo date={new Date(c.created_at)} locale="ja" />
            </div>
            <p>
              <SimpleTextFormat content={c.content} />
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
};
